import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';
import { UnitTypePicker } from './UnitTypePicker';
import type { HierarchyPageVM } from './useHierarchyPage';

export interface OrgCreateModalProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: "+ Đơn vị con" — create an `Organization` under the selected node.
 *
 * Replaces the pre-rebuild `TenantModal`, which created an `OrganizationAppScope`
 * row (an ACH tenant) and could not take a parent at all. Tree nodes are real
 * organizations now, so this creates one.
 *
 * TWO CALLS, and that is the backend's shape rather than a shortcut:
 * `OrganizationCreateSerializer` accepts only {code, name, branding} — there is
 * no `parent` at creation time — so the org is created and then PATCHed under its
 * parent. `useHierarchyPage.createChildOrg` reports the in-between state
 * explicitly if the second call fails, because the org does exist by then, at
 * root, and pretending otherwise would leave an orphan the admin never looks for.
 *
 * Stays a modal (unlike rename, which frame 30 puts inline) because creation
 * needs a second field — the code, which is immutable afterwards — plus the
 * parent context line, and none of that fits in a row.
 *
 * EAA-1818: layout below now matches frame 28 exactly — subtitle line, Tên đơn
 * vị / Mã đơn vị (required, mã monospace with its auto-slug hint), a READ-ONLY
 * "Cấp cha" display (never an editable control — the parent is fixed by which
 * row's "+ Đơn vị con" the admin used to open this modal) carrying the parent's
 * own type badge, then the "Loại đơn vị" picker, and a "Ghi vào nhật ký" footer
 * note.
 *
 * EAA-1838: that picker is now REQUIRED and can create its own option inline —
 * it replaced an optional "Cấp bậc (tier)" select. Two changes, one reason each:
 *
 *   - REQUIRED, because an optional classification is what let the reparent rule
 *     skip itself on most real moves. The API still accepts an absent type (the
 *     first organization has no root to draw a vocabulary from), but this screen
 *     always knows the root, so it always asks.
 *   - INLINE CREATE, because the old flow made the admin leave this modal to
 *     author a rung in a separate panel and come back. See `UnitTypePicker` for
 *     the full rationale.
 */
export function OrgCreateModal({ vm }: OrgCreateModalProps) {
    const { t } = useTranslation();
    const {
        createOpen,
        createForm,
        createError,
        closeCreate,
        onCreateNameChange,
        setCreateForm,
        onSubmitCreate,
        createChildOrg,
        activeUnitTypes,
        unitTypesById,
        canManageTree,
        createInlineUnitType,
        createUnitType,
    } = vm;

    // EAA-1818: the parent's own type, resolved for the "Cấp cha" badge — the
    // parent was fixed when this modal opened (`openCreateChild` snapshots
    // `parentUnitTypeId` from the node clicked), so this is a lookup, not a choice.
    const parentUnitType = createForm.parentUnitTypeId
        ? unitTypesById.get(createForm.parentUnitTypeId)
        : undefined;

    // EAA-1838: offer the ACTIVE vocabulary, but never drop a hidden type that is
    // already selected — a controlled <select> whose value is missing from its
    // options silently renders as something else, which here would mean creating
    // the đơn vị as the wrong kind.
    const selected = createForm.unitTypeId
        ? unitTypesById.get(createForm.unitTypeId)
        : undefined;
    const unitTypeOptions =
        selected && !selected.is_active ? [...activeUnitTypes, selected] : activeUnitTypes;

    return (
        <Modal
            open={createOpen}
            onCancel={closeCreate}
            title={t('console.hierarchy.createModal.title')}
            footer={
                <div className="hz-move-footer">
                    <span className="hz-move-footer-note">
                        {t('console.hierarchy.auditLogNote')}
                    </span>
                    <span className="hz-move-footer-actions">
                        <button type="button" className="portal-btn" onClick={closeCreate}>
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            form="hierarchy-create-org-form"
                            className="portal-btn portal-btn-primary"
                            disabled={createChildOrg.isPending}
                        >
                            {createChildOrg.isPending
                                ? t('console.hierarchy.createModal.creatingLabel')
                                : t('console.hierarchy.createModal.submitButton')}
                        </button>
                    </span>
                </div>
            }
        >
            <p className="portal-admin-hint">
                {t('console.hierarchy.createModal.parentHint', {
                    parentName:
                        createForm.parentName ||
                        t('console.hierarchy.moveConfirmDialog.rootFallback'),
                })}
            </p>

            <form
                id="hierarchy-create-org-form"
                className="portal-admin-form"
                onSubmit={onSubmitCreate}
            >
                {/* EAA-1818: the caption and its "*" MUST sit in one wrapping span.
                    `.portal-admin-form label` is a column flex container, so a bare
                    text node and a sibling span are two flex items and the marker
                    drops onto its own line. Same wrapper `profile/FormField.tsx`
                    uses for exactly this reason. */}
                <label>
                    <span className="portal-field-label">
                        {t('console.hierarchy.createModal.nameLabel')}
                        <span className="portal-field-required" aria-hidden="true">
                            {' '}
                            *
                        </span>
                    </span>
                    <input
                        type="text"
                        placeholder={t('console.hierarchy.createModal.namePlaceholder')}
                        value={createForm.name}
                        onChange={(e) => onCreateNameChange(e.target.value)}
                        required
                    />
                </label>

                <label>
                    <span className="portal-field-label">
                        {t('console.hierarchy.createModal.codeLabel')}
                        <span className="portal-field-required" aria-hidden="true">
                            {' '}
                            *
                        </span>
                    </span>
                    <input
                        type="text"
                        className="hz-code-input"
                        placeholder={t('console.hierarchy.createModal.codePlaceholder')}
                        value={createForm.code}
                        onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })}
                        required
                    />
                </label>
                <p className="portal-admin-hint">{t('console.hierarchy.createModal.codeHint')}</p>

                <label>
                    <span className="portal-field-label">
                        {t('console.hierarchy.createModal.parentLabel')}
                    </span>
                    {/* EAA-1818: read-only by design — the parent is fixed by which
                        row's "+ Đơn vị con" opened this modal, not chosen here. */}
                    <div className="hz-readonly-field">
                        {parentUnitType && (
                            <span className="hz-unit-type-tag">{parentUnitType.name}</span>
                        )}
                        <span>
                            {createForm.parentName ||
                                t('console.hierarchy.createModal.parentRootFallback')}
                        </span>
                    </div>
                </label>

                <UnitTypePicker
                    options={unitTypeOptions}
                    value={createForm.unitTypeId}
                    onChange={(unitTypeId) => setCreateForm({ ...createForm, unitTypeId })}
                    canCreate={canManageTree}
                    parentUnitTypeId={createForm.parentUnitTypeId}
                    onCreate={createInlineUnitType}
                    creating={createUnitType.isPending}
                    disabled={createChildOrg.isPending}
                />
                <p className="portal-admin-hint">
                    {t('console.hierarchy.createModal.unitTypeHint')}
                </p>

                {createError && <p className="portal-admin-error">{createError}</p>}
            </form>
        </Modal>
    );
}

export default OrgCreateModal;
